/**
 * Single-property location map — Leaflet + OpenStreetMap tiles, matching the
 * tile source `PublicPropertyDetailsScreen` uses in the Flutter app
 * (`https://tile.openstreetmap.org/{z}/{x}/{y}.png`). Renders nothing when
 * the property has no coordinates, same as the Flutter screen's "Location
 * coordinates not available" fallback (handled by the caller, not this
 * component, so each page controls its own empty-state copy).
 */
import { MapContainer, Marker, Popup, TileLayer } from 'react-leaflet';
import L from 'leaflet';
import markerIcon2x from 'leaflet/dist/images/marker-icon-2x.png';
import markerIcon from 'leaflet/dist/images/marker-icon.png';
import markerShadow from 'leaflet/dist/images/marker-shadow.png';
import 'leaflet/dist/leaflet.css';

// Vite serves node_modules images as hashed URLs, not the relative paths
// Leaflet's default icon URLs assume — without this, markers render as
// broken images. A one-time module-level fix, same as react-leaflet's own
// documented workaround.
const defaultIcon = L.icon({
  iconUrl: markerIcon,
  iconRetinaUrl: markerIcon2x,
  shadowUrl: markerShadow,
  iconSize: [25, 41],
  iconAnchor: [12, 41],
  popupAnchor: [1, -34],
  shadowSize: [41, 41],
});

export interface PropertyMapProps {
  latitude: number;
  longitude: number;
  label: string;
}

export function PropertyMap({ latitude, longitude, label }: PropertyMapProps): React.JSX.Element {
  return (
    <MapContainer
      center={[latitude, longitude]}
      zoom={15}
      scrollWheelZoom={false}
      className="property-map"
    >
      <TileLayer
        attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
        url="https://tile.openstreetmap.org/{z}/{x}/{y}.png"
      />
      <Marker position={[latitude, longitude]} icon={defaultIcon}>
        <Popup>{label}</Popup>
      </Marker>
    </MapContainer>
  );
}

export default PropertyMap;
